import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import api from "@/lib/api";

const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

function isValidPhone(p) {
  return p.replace(/\D/g, "").length === 10;
}

function formatPhone(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function Contact() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setErrors((prev) => ({ ...prev, [k]: "" })); };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Required";
    if (!form.last_name.trim()) errs.last_name = "Required";
    if (!form.email.trim()) errs.email = "Required";
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Enter a valid email address";
    if (!form.phone.trim()) errs.phone = "Required";
    else if (!isValidPhone(form.phone)) errs.phone = "Invalid phone number";
    if (!form.subject.trim()) errs.subject = "Required";
    if (!form.message.trim()) errs.message = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Message sent! We'll get back to you shortly.");
    } catch (err) {
      const msg = err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Failed to send. Please try again.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ink text-white min-h-screen">
      <Navigation />
      <section className="relative pt-32 md:pt-40 pb-32 px-5 sm:px-8 max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.0 }}>
          <p className="overline mb-5" style={{ color: "rgba(170,170,170,0.85)" }}>Get in touch</p>
          <h1 className="font-semibold tracking-[-0.045em] leading-[0.95] mb-8" style={{ fontSize: "clamp(2.6rem, 8vw, 5rem)", color: "#f5f5f7" }}>
            Contact <em className="italic" style={{ fontFamily: "'Instrument Serif', 'Fraunces', Georgia, serif", background: "var(--gold-gradient)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Nisky Auto</em>
          </h1>

          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-card-premium p-10 text-center">
              <CheckCircle size={40} className="mx-auto mb-4" style={{ color: "#8A8A8A" }} />
              <h2 className="text-2xl font-semibold mb-2">Thank you!</h2>
              <p style={{ color: "rgba(245,245,247,0.75)" }}>Your message has been sent. We'll respond within 24 hours.</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="glass-card p-6 md:p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "rgba(245,245,247,0.7)" }}>First name *</label>
                  <input className="input-luxe w-full" value={form.first_name} onChange={set("first_name")} required placeholder="John" style={errors.first_name ? {borderColor:"#F44336"} : {}} />
                  {errors.first_name && <p className="flex items-center gap-1 mt-1" style={{color:"#F44336",fontSize:11}}><AlertCircle size={10} />{errors.first_name}</p>}
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "rgba(245,245,247,0.7)" }}>Last name *</label>
                  <input className="input-luxe w-full" value={form.last_name} onChange={set("last_name")} required placeholder="Doe" style={errors.last_name ? {borderColor:"#F44336"} : {}} />
                  {errors.last_name && <p className="flex items-center gap-1 mt-1" style={{color:"#F44336",fontSize:11}}><AlertCircle size={10} />{errors.last_name}</p>}
                </div>
              </div>
              <div>
                  <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "rgba(245,245,247,0.7)" }}>Email *</label>
                <input type="email" className="input-luxe w-full" value={form.email} onChange={set("email")} required placeholder="john@example.com" style={errors.email ? {borderColor:"#F44336"} : {}} />
                {errors.email && <p className="flex items-center gap-1 mt-1" style={{color:"#F44336",fontSize:11}}><AlertCircle size={10} />{errors.email}</p>}
              </div>
              <div>
                  <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "rgba(245,245,247,0.7)" }}>Phone *</label>
                <input type="tel" className="input-luxe w-full" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))} required placeholder="(518) 555-0123" style={errors.phone ? {borderColor:"#F44336"} : {}} />
                {errors.phone && <p className="flex items-center gap-1 mt-1" style={{color:"#F44336",fontSize:11}}><AlertCircle size={10} />{errors.phone}</p>}
              </div>
              <div>
                  <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "rgba(245,245,247,0.7)" }}>Subject *</label>
                <input className="input-luxe w-full" value={form.subject} onChange={set("subject")} required placeholder="What is this about?" style={errors.subject ? {borderColor:"#F44336"} : {}} />
                {errors.subject && <p className="flex items-center gap-1 mt-1" style={{color:"#F44336",fontSize:11}}><AlertCircle size={10} />{errors.subject}</p>}
              </div>
              <div>
                  <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "rgba(245,245,247,0.7)" }}>Message *</label>
                <textarea className="input-luxe w-full" rows={5} value={form.message} onChange={set("message")} required placeholder="Tell us more..." style={{ paddingLeft: 18, ...(errors.message ? {borderColor:"#F44336"} : {}) }} />
                {errors.message && <p className="flex items-center gap-1 mt-1" style={{color:"#F44336",fontSize:11}}><AlertCircle size={10} />{errors.message}</p>}
              </div>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} type="submit" disabled={sending} className="btn-primary w-full justify-center">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? "Sending..." : "Send Message"}
              </motion.button>
            </form>
          )}
        </motion.div>
      </section>
      <Footer />
    </div>
  );
}
